import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, X } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

const PROVIDER_META = {
  201:  { name: "Supernova",   emoji: "🎲", color: "#7c3aed", bg: "linear-gradient(135deg,#4c1d95,#7c3aed)" },
  1036: { name: "Reel Games",  emoji: "🎰", color: "#16a34a", bg: "linear-gradient(135deg,#14532d,#16a34a)" },
};
const HIDDEN_PROVIDERS = new Set([1030]);

function GamesPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  const [gameList, setGameList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProvider, setActiveProvider] = useState(null);
  const [launching, setLaunching] = useState(null);
  const [lobbyURL, setLobbyURL] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    API.get("/api/games/list")
      .then((res) => {
        const list = (res.data?.gameList || []).filter((g) => !HIDDEN_PROVIDERS.has(g.gameCode));
        setGameList(list);
        if (list.length > 0) setActiveProvider(list[0].gameCode);
      })
      .catch(() => setGameList([]))
      .finally(() => setLoading(false));
  }, []);

  const launchGame = async (tpGameId, tableCode, tableName) => {
    if (!user?.id) { navigate("/login"); return; }
    setLaunching(`${tpGameId}-${tableCode}`);
    setError("");
    try {
      const res = await API.post("/api/game/launch", { user_id: user.id, tpGameId, tableCode });
      setLobbyURL(res.data.lobbyURL);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to launch ${tableName}`);
    } finally {
      setLaunching(null);
    }
  };

  const launchReelGamesLobby = async () => {
    if (!user?.id) { navigate("/login"); return; }
    setLaunching("reel-lobby");
    setError("");
    try {
      const res = await API.post("/api/reelgames/launch", { user_id: user.id });
      setLobbyURL(res.data.lobbyURL);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to launch Reel Games");
    } finally {
      setLaunching(null);
    }
  };

  if (lobbyURL) {
    return (
      <div style={iframeWrap}>
        <div style={iframeBar}>
          <span style={iframeTitle}>🎮 GAMES</span>
          <button type="button" style={iframeClose} onClick={() => setLobbyURL(null)}>
            <X size={20} color="#fff" />
          </button>
        </div>
        <iframe src={lobbyURL} title="Game" style={iframeEl} allow="autoplay; fullscreen; payment" allowFullScreen />
      </div>
    );
  }

  const activeGame = gameList.find((g) => g.gameCode === activeProvider);
  const allTables = activeGame?.data || [];
  const filteredTables = filter === "all" ? allTables : allTables.filter((t) => t.type === filter);

  return (
    <div style={page}>
      <header style={hdr}>
        <button type="button" style={backBtn} onClick={() => navigate("/dashboard")}>
          <ChevronLeft size={22} color="#fff" />
        </button>
        <span style={hdrTitle}>🎮 Games</span>
        <div style={{ width: 38 }} />
      </header>

      {/* Provider tabs */}
      <div style={providerRow}>
        {gameList.map((g) => {
          const meta = PROVIDER_META[g.gameCode] || { name: g.gameName, emoji: "🎲", color: "#6366f1", bg: "linear-gradient(135deg,#4338ca,#6366f1)" };
          const active = activeProvider === g.gameCode;
          return (
            <button key={g.gameCode} type="button" style={active ? { ...provTab, ...provTabActive, background: meta.bg } : provTab} onClick={() => setActiveProvider(g.gameCode)}>
              <span style={provEmoji}>{meta.emoji}</span>
              <span style={active ? { ...provName, color: "#fff" } : provName}>{meta.name}</span>
              <span style={provCount}>{g.data?.length || 0}</span>
            </button>
          );
        })}

        {/* ReelGames tab (always show even if not in game list) */}
        {!gameList.find((g) => g.gameCode === 1036) && (
          <button type="button" style={activeProvider === 1036 ? { ...provTab, ...provTabActive, background: PROVIDER_META[1036].bg } : provTab} onClick={() => setActiveProvider(1036)}>
            <span style={provEmoji}>🎰</span>
            <span style={activeProvider === 1036 ? { ...provName, color: "#fff" } : provName}>Reel Games</span>
          </button>
        )}
      </div>

      {error && <div style={errBox}>{error}</div>}

      {loading ? (
        <div style={loadWrap}><div style={spinner} />Loading games…</div>
      ) : activeProvider === 1036 && !gameList.find((g) => g.gameCode === 1036) ? (
        /* ReelGames lobby fallback */
        <div style={reelLobbyWrap}>
          <div style={reelLobbyCard}>
            <div style={reelLobbyIcon}>🎰</div>
            <h2 style={reelLobbyTitle}>Reel Games</h2>
            <p style={reelLobbySub}>Slot machines, reels &amp; more — tap below to open the full lobby</p>
            <button type="button" style={launching === "reel-lobby" ? reelBtnDisabled : reelBtn} onClick={launchReelGamesLobby} disabled={!!launching}>
              {launching === "reel-lobby" ? "Launching…" : "🎰 Open Reel Games Lobby"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Type filter */}
          <div style={filterRow}>
            {["all", "live", "virtual"].map((f) => (
              <button key={f} type="button" style={filter === f ? filterBtnActive : filterBtn} onClick={() => setFilter(f)}>
                {f === "all" ? "All" : f === "live" ? "🔴 Live" : "🤖 Virtual"}
              </button>
            ))}
          </div>

          {/* Game grid */}
          <div style={gameGrid}>
            {filteredTables.map((table) => {
              const key = `${activeProvider}-${table.tableCode}`;
              const isLaunching = launching === key;
              const hasImg = table.imageUrl && table.imageUrl !== "NULL" && table.imageUrl !== "null";
              return (
                <button key={table.tableCode} type="button" style={gameCard} onClick={() => launchGame(activeProvider, table.tableCode, table.tableName)} disabled={!!launching}>
                  <div style={gameImgWrap}>
                    {hasImg
                      ? <img src={table.imageUrl} alt={table.tableName} style={gameImg} onError={(e) => { e.target.style.display = "none"; }} />
                      : <div style={gameImgFallback}>{PROVIDER_META[activeProvider]?.emoji || "🎲"}</div>
                    }
                    <span style={table.type === "live" ? liveBadge : virtualBadge}>{table.type === "live" ? "LIVE" : "RNG"}</span>
                    {isLaunching && <div style={launchingOverlay}><div style={spinner} /></div>}
                  </div>
                  <div style={gameCardBody}>
                    <span style={gameName}>{table.tableName}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
}

const page        = { minHeight: "100vh", background: "#0d0d1a", fontFamily: "Inter, system-ui, sans-serif", color: "#fff", paddingBottom: 80 };
const hdr         = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", height: 54, background: "#111128", borderBottom: "1px solid rgba(255,255,255,0.07)", position: "sticky", top: 0, zIndex: 50 };
const backBtn     = { border: 0, background: "transparent", cursor: "pointer", padding: 4, display: "grid", placeItems: "center" };
const hdrTitle    = { fontSize: 18, fontWeight: 900, letterSpacing: "0.05em" };

const providerRow = { display: "flex", gap: 8, padding: "12px 14px", overflowX: "auto", scrollbarWidth: "none", flexShrink: 0 };
const provTab     = { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 14px", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, background: "rgba(255,255,255,0.05)", cursor: "pointer", flexShrink: 0, minWidth: 72 };
const provTabActive = { border: "1.5px solid rgba(255,255,255,0.3)", boxShadow: "0 4px 16px rgba(0,0,0,0.4)" };
const provEmoji   = { fontSize: 22, lineHeight: 1 };
const provName    = { fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.6)", textAlign: "center", letterSpacing: "0.03em" };
const provCount   = { fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 600 };

const filterRow   = { display: "flex", gap: 8, padding: "4px 14px 10px", overflowX: "auto", scrollbarWidth: "none" };
const filterBtn   = { height: 30, padding: "0 14px", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, background: "transparent", color: "rgba(255,255,255,0.55)", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 };
const filterBtnActive = { ...filterBtn, background: "#6366f1", border: "1.5px solid #818cf8", color: "#fff" };

const errBox      = { margin: "0 14px 10px", padding: "10px 14px", borderRadius: 12, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", fontSize: 13, fontWeight: 700 };

const gameGrid    = { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, padding: "0 14px" };
const gameCard    = { border: 0, background: "rgba(255,255,255,0.04)", borderRadius: 14, overflow: "hidden", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column" };
const gameImgWrap = { position: "relative", width: "100%", aspectRatio: "16/9", background: "#1a1a2e", overflow: "hidden" };
const gameImg     = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const gameImgFallback = { width: "100%", height: "100%", display: "grid", placeItems: "center", fontSize: 36 };
const liveBadge   = { position: "absolute", top: 6, left: 6, fontSize: 9, fontWeight: 900, background: "#ef4444", color: "#fff", padding: "2px 6px", borderRadius: 4, letterSpacing: "0.06em" };
const virtualBadge= { ...liveBadge, background: "#6366f1" };
const launchingOverlay = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center" };
const gameCardBody= { padding: "8px 10px 10px" };
const gameName    = { fontSize: 12, fontWeight: 800, color: "#e2e8f0", lineHeight: 1.3, display: "block" };

const loadWrap    = { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 40, color: "rgba(255,255,255,0.5)", fontSize: 14 };
const spinner     = { width: 28, height: 28, border: "3px solid rgba(255,255,255,0.1)", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.8s linear infinite" };

const reelLobbyWrap = { padding: "24px 14px" };
const reelLobbyCard = { borderRadius: 20, border: "1px solid rgba(22,163,74,0.3)", background: "linear-gradient(160deg,#0a1a0a,#0f3a0f)", padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" };
const reelLobbyIcon = { fontSize: 64 };
const reelLobbyTitle= { margin: 0, fontSize: 28, fontWeight: 900, color: "#4ade80" };
const reelLobbySub  = { margin: 0, fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.6 };
const reelBtn       = { height: 52, padding: "0 28px", border: 0, borderRadius: 14, background: "linear-gradient(135deg,#16a34a,#15803d)", color: "#fff", fontSize: 16, fontWeight: 900, cursor: "pointer", boxShadow: "0 6px 20px rgba(22,163,74,0.4)" };
const reelBtnDisabled = { ...reelBtn, background: "#1e2a1e", color: "#475569", boxShadow: "none", cursor: "not-allowed" };

const iframeWrap  = { position: "fixed", inset: 0, zIndex: 9999, display: "flex", flexDirection: "column", background: "#000" };
const iframeBar   = { height: 46, background: "#111128", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", borderBottom: "1px solid rgba(255,255,255,0.08)" };
const iframeTitle = { fontSize: 15, fontWeight: 900, color: "#a78bfa" };
const iframeClose = { border: 0, background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", padding: 6 };
const iframeEl    = { flex: 1, border: "none", width: "100%", minHeight: 0, display: "block" };

export default GamesPage;
